import type { FC, PropsWithChildren } from 'react'
import { createContext, useContext, useEffect, useMemo, useState } from 'react'

import { useUnit } from 'effector-react'
import { usePersistentState } from 'react-persistent-state'

import { $fanId } from '@/models/chattingUI/chats/chats.ts'
import { useExternalLinksListener } from '@/modules/chatting/hooks/use-external-links-listener.ts'
import { usePusherListener } from '@/modules/chatting/hooks/use-pusher-listener.ts'
import {
  ChatsListContext,
  useComputeChatsListContext,
} from '@/modules/chatting/state/chats-provider.ts'
import { SentMediaMessageProvider } from '@/modules/chatting/state/sent-media-message-provider.tsx'
import {
  TabsContext,
  useComputeTabs,
} from '@/modules/chatting/state/tabs-provider.ts'

import { useChatterActivityTracking } from '../hooks/use-chatter-activity-tracking.ts'
import type { ChattingContextValue, Message, OnlineCountConfig } from '../types'
import { defaultHighlightOption } from '../ui/header/creator-hero-settings-popup.tsx'
import {
  CountdownsContextProvider,
  useComputeCountdowns,
} from './countdown-provider.tsx'

const ChattingContext = createContext<ChattingContextValue | null>(null)

export const ChattingProvider: FC<PropsWithChildren> = ({ children }) => {
  const fanId = useUnit($fanId)

  const [replyToMessage, setReplyToMessage] = useState<Message>()
  const [highlightedNumber, setHighlightedNumber] = usePersistentState(
    defaultHighlightOption.id,
    { storageKey: 'highlighting-numbered-fans' },
  )
  const [isScriptAutoSuggestionEnabled, setIsScriptAutoSuggestionEnabled] =
    usePersistentState(true, {
      storageKey: 'script-auto-suggestion-enabled',
    })
  const [onlineCount, setOnlineCount] =
    usePersistentState<OnlineCountConfig | null>(null, {
      storageKey: 'online-count',
    })

  const tabsContext = useComputeTabs()
  const { activeTabsFanId, setTabFan, tabs } = tabsContext

  const chatListContextValues = useComputeChatsListContext(tabs, setTabFan)

  const { openChat } = chatListContextValues

  const countdownContextValue = useComputeCountdowns({
    openChat,
  })

  useChatterActivityTracking()

  useEffect(() => {
    setReplyToMessage(undefined)
  }, [fanId])

  usePusherListener({
    handleOnlineCountUpdate: setOnlineCount,
  })

  useExternalLinksListener()

  const value: ChattingContextValue = useMemo(
    () => ({
      onlineCount,
      highlightedNumber,
      setHighlightedNumber,
      replyToMessage,
      setReplyToMessage,
      isScriptAutoSuggestionEnabled,
      setIsScriptAutoSuggestionEnabled,
    }),
    [
      onlineCount,
      highlightedNumber,
      setHighlightedNumber,
      replyToMessage,
      setReplyToMessage,
      isScriptAutoSuggestionEnabled,
      setIsScriptAutoSuggestionEnabled,
    ],
  )

  return (
    <ChattingContext.Provider value={value}>
      <TabsContext.Provider value={tabsContext}>
        <ChatsListContext.Provider value={chatListContextValues}>
          <CountdownsContextProvider values={countdownContextValue}>
            <SentMediaMessageProvider fanId={activeTabsFanId}>
              {children}
            </SentMediaMessageProvider>
          </CountdownsContextProvider>
        </ChatsListContext.Provider>
      </TabsContext.Provider>
    </ChattingContext.Provider>
  )
}

export const useChatting = () => {
  const context = useContext(ChattingContext)

  if (!context) {
    throw new Error('useChatting must be used within ChattingProvider')
  }

  return context
}
